import {
    Input,
    DatePicker,
    Button,
    Space,
    Row,
    Col,
    Typography,
    Progress,
    Divider,
} from "antd";
import { memo, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { PlusOutlined } from "@ant-design/icons";
import RevinitiAntTableWidget from "@components/widgets/RevinitiAntTableWidget";
import { getCampaignCreationTableConfig } from "@/ts/reviniti/widget-configs";
import {
    addCollection,
    deleteCollection,
    updateClusterFormData,
    updateCollection,
} from "@/features/clustersSlice";
import {
    IChannel,
    ICollection,
    ICreateCollectionsFormProps,
    KeywordTypeValues,
} from "@/ts/common-types";
import { 
    disableCampaignsAddition, 
    isDisabledDate,
    calculateAutoSuggestBudgets,
} from "@ts/utils";
import CampaignCollectionBreakdown from "./CampaignCollectionBreakdown";
import store from "@/store";
import {
    internationalizeNumericValue,
    parseInternationalizedValue,
    validateMaxCampaignBudget,
} from "@ts/utils";

const { Title, Text } = Typography;

enum COLLECTION_FORM_FIELDS {
    BUDGET = "budget",
    START_DATE = "startDate",
}

const CreateCollectionsForm: React.FC<ICreateCollectionsFormProps> = memo(
    ({
        clusterId,
        cluster,
        handleFieldChange,
        handleAddKeywords,
        handleEditCohort,
        setSelectedKeywords,
        citiesList,
        currentStep,
        handleAddAssets,
    }) => {
        const dispatch = useDispatch();
        const [isRowExpanded, setIsRowExpanded] = useState<boolean>(false);
        const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);
        const [touchedFields, setTouchedFields] = useState<{
            [key: string]: {
                budget?: boolean;
                startDate?: boolean;
            };
        }>({});

        const markFieldAsTouched = (field: COLLECTION_FORM_FIELDS) => {
            setTouchedFields(prev => ({
                ...prev,
                [clusterId]: {
                    ...prev[clusterId],
                    [field]: true,
                },
            }));
        };

        const isFieldTouched = (field: COLLECTION_FORM_FIELDS) => {
            return touchedFields[clusterId]?.[field] || false;
        };

        const budgetCalculations = useMemo(() => {
            const totalBudget =
                cluster?.collections?.reduce(
                    (acc, collection) => acc + (Number(collection.budget) || 0),
                    0
                ) || 0;

            const clusterTotalBudget = Number(cluster?.totalBudget) || 0;
            const percentage =
                clusterTotalBudget > 0
                    ? (totalBudget / clusterTotalBudget) * 100
                    : 0;

            const isOverBudget = totalBudget > clusterTotalBudget;

            return {
                totalBudget,
                clusterTotalBudget,
                percentage,
                isOverBudget,
            };
        }, [cluster?.collections, cluster?.totalBudget]);

        const disabledAddRow = useMemo(() => {
            if (!cluster?.collections?.length) return false;
            return disableCampaignsAddition(
                cluster?.collections?.[cluster?.collections?.length - 1]
            );
        }, [cluster?.collections]);

        const handleRowExpand = () => {
            setIsRowExpanded((prev) => !prev);
        };


        const handleAddRow = () => {
            const newCollection = {
                campaignType: "",
                keywordType: "",
                keywords: [] as string[],
                city: "",
                timeSlot: "",
                budget: "",
                type: "",
            };
            dispatch(
                addCollection({
                    clusterId,
                    collection: newCollection,
                })
            );
        };

        const handleDelete = (collectionId: string) => {
            dispatch(
                deleteCollection({
                    clusterId,
                    collectionId,
                })
            );
        };

        const handleDuplicate = (collection: ICollection) => {
            const { ...collectionWithoutId } = collection;
            dispatch(
                addCollection({
                    clusterId,
                    collection: collectionWithoutId,
                })
            );
        };

        const handleCampaignUpdate = (
            collectionId: string,
            campaign: Record<string, any>,
            updates: Record<string, any>
        ) => {
            const collection = cluster?.collections?.find(
                (c: ICollection) => c.id === collectionId
            );

            if (!collection) return;

            const updatedCampaigns = (collection.campaigns || []).map(
                (c: any) => (c.id === campaign.id ? { ...c, ...updates } : c)
            );

            // Calculate collection budget based on campaign budgets if budget is being updated
            const collectionBudgetUpdate: Record<string, any> = {};
            if (updates.budget !== undefined) {
                // Calculate sum of all non-archived campaign budgets
                const sumOfCampaignBudgets = updatedCampaigns
                    .filter((c: any) => !c.isArchived)
                    .reduce((sum: number, c: any) => {
                        const budgetValue = Number(c.budget) || 0;
                        return sum + budgetValue;
                    }, 0);

                const currentCollectionBudget = Number(collection.budget) || 0;

                // If sum > totalBudget, update totalBudget to equal sum
                // If sum <= totalBudget, keep totalBudget the same
                if (sumOfCampaignBudgets > currentCollectionBudget) {
                    collectionBudgetUpdate.budget =
                        sumOfCampaignBudgets.toString();
                }
                // Otherwise, keep the current budget (no update needed)
            }

            dispatch(
                updateCollection({
                    clusterId,
                    collectionId,
                    updates: {
                        campaigns: updatedCampaigns,
                        ...collectionBudgetUpdate,
                    },
                })
            );
        };

        const handleAutoSuggestBudget = (
            targetClusterId: string,
            targetCollectionId?: string
        ) => {
            const currentState = store.getState();
            const allClusters = currentState?.clusters?.clusters || [];
            const targetCluster = allClusters.find((c) => c.id === targetClusterId);

            if (!targetCluster || !citiesList || citiesList.length === 0) {
                console.warn('Auto-suggest budget: Missing required data', {
                    hasCluster: !!targetCluster,
                    hasCitiesList: !!citiesList,
                    citiesListLength: citiesList?.length,
                    clusterId: targetClusterId
                });
                return;
            }

            // Find the target collection and get its budget
            const targetCollection = targetCluster.collections?.find((c: any) => c.id === targetCollectionId);
            const campaignBudget = Number(targetCollection?.budget) || 0;

            if (!campaignBudget || campaignBudget <= 0) {
                console.warn('Auto-suggest budget: Campaign budget is empty or zero', {
                    collectionId: targetCollectionId,
                    campaignBudget
                });
                return;
            }

            const results = calculateAutoSuggestBudgets(
                targetCluster,
                citiesList,
                targetCollectionId,
                campaignBudget,
            );

            results.forEach((result) => {
                dispatch(
                    updateCollection({
                        clusterId: targetClusterId,
                        collectionId: result.collectionId,
                        updates: {
                            campaigns: result.campaigns,
                            budget: result.collectionBudget.toString(),
                        },
                    })
                );
            });
        };

        const clientCurrencySymbol = useSelector(
            (state: IChannel) => state.channelCurrencySymbol.value
        );

        // Field validation configuration
        const fieldValidators: Record<COLLECTION_FORM_FIELDS, () => boolean> = {
            budget: () => !cluster?.totalBudget || Number(cluster?.totalBudget) === 0,
            startDate: () => !cluster?.startDate,
        };

        const fieldErrorMessages: Record<COLLECTION_FORM_FIELDS, string> = {
            budget: 'Collection budget is required',
            startDate: 'Start date is required',
        };

        // Generic field validator - checks if touched AND invalid
        const isFieldInvalid = (field: COLLECTION_FORM_FIELDS): boolean => {
            return isFieldTouched(field) && fieldValidators[field]();
        };

        // Reusable error message component
        const ErrorMessage = ({ field }: { field: COLLECTION_FORM_FIELDS }) => {
            if (!isFieldInvalid(field)) return null;
            
            return (
                <div className="fs-8 text-danger fw-500" style={{ marginTop: "4px" }}>
                    {fieldErrorMessages[field]}
                </div>
            );
        };

        const isBudgetInvalid = isFieldInvalid(COLLECTION_FORM_FIELDS.BUDGET);
        const isStartDateInvalid = isFieldInvalid(COLLECTION_FORM_FIELDS.START_DATE);

        return (
            <Row style={{ width: "100%" }}>
                <Col span={24} className="collections-form-container">
                    {/* Collection Budget Section */}
                    <Row className="w-100" gutter={24}>
                        <Col span={4}>
                            <Title className="fs-7">Collection Budget</Title>
                            <div>
                                <Input
                                    type="text"
                                    className="w-100"
                                    prefix={<Text>{clientCurrencySymbol}</Text>}
                                    value={
                                        cluster?.totalBudget
                                            ? internationalizeNumericValue(
                                                  cluster.totalBudget
                                              )
                                            : ""
                                    }
                                    style={{ 
                                        width: 275, 
                                        height: 36,
                                        borderColor: isBudgetInvalid ? '#ff4d4f' : undefined
                                    }}
                                    status={isBudgetInvalid ? 'error' : undefined}
                                    onChange={(e) => {
                                        const numericValue =
                                            parseInternationalizedValue(
                                                e.target.value
                                            );
                                        if (!validateMaxCampaignBudget(numericValue)) {
                                            return;
                                        }
                                        dispatch(
                                            updateClusterFormData({
                                                clusterId,
                                                updates: {
                                                    totalBudget: isNaN(numericValue)
                                                        ? ""
                                                        : String(numericValue),
                                                },
                                            })
                                        );
                                    }}
                                    onBlur={() => markFieldAsTouched(COLLECTION_FORM_FIELDS.BUDGET)}
                                />
                                <ErrorMessage field={COLLECTION_FORM_FIELDS.BUDGET} />
                            </div>
                        </Col>
                        <Col span={4}>
                            <Title className="fs-7">Start Date</Title>
                            <div>
                                <DatePicker
                                    className="w-100"
                                    style={{ 
                                        height: 36,
                                        borderColor: isStartDateInvalid ? '#ff4d4f' : undefined
                                    }}
                                    status={isStartDateInvalid ? 'error' : undefined}
                                    value={cluster?.startDate || null}
                                    format="DD/MM/YYYY"
                                    disabledDate={isDisabledDate}
                                    onChange={(date) => {
                                        dispatch(
                                            updateClusterFormData({
                                                clusterId,
                                                updates: {
                                                    startDate: date,
                                                },
                                            })
                                        );
                                    }}
                                    onBlur={() => markFieldAsTouched(COLLECTION_FORM_FIELDS.START_DATE)}
                                    placeholder=""
                                />
                                <ErrorMessage field={COLLECTION_FORM_FIELDS.START_DATE} />
                            </div>
                        </Col>
                        <Col span={4}>
                            <Title className="fs-7">End Date</Title>
                            <DatePicker
                                className="w-100"
                                style={{ height: 36 }}
                                value={cluster?.endDate || null}
                                disabled={true}
                                onChange={(date) =>
                                    dispatch(
                                        updateClusterFormData({
                                            clusterId,
                                            updates: {
                                                endDate: date,
                                            },
                                        })
                                    )
                                }
                                placeholder=""
                            />
                        </Col>
                        <Col span={8}>
                            <Title className="fs-7">Budget Used</Title>
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 8,
                                    height: 36,
                                }}
                            >
                                {/* Budget text on the left */}
                                <span
                                    className="fs-7 fw-700"
                                    style={{ whiteSpace: "nowrap" }}
                                >
                                    <span className="text-info">
                                        {`${clientCurrencySymbol} ${internationalizeNumericValue(
                                            budgetCalculations.totalBudget
                                        )}`}
                                    </span>
                                    <span className="text-gray">
                                        {` / ${clientCurrencySymbol} ${internationalizeNumericValue(
                                            budgetCalculations.clusterTotalBudget
                                        )}`}
                                    </span>
                                </span>

                                <Progress
                                    percent={budgetCalculations.percentage}
                                    strokeColor={
                                        budgetCalculations.isOverBudget
                                            ? "#ff4d4f"
                                            : "#1890ff"
                                    }
                                    format={(percent) => {
                                        return `${Math.round(percent)}%`;
                                    }}
                                    percentPosition={{
                                        align: "end",
                                        type: "outer",
                                    }}
                                    style={{ flex: 1, margin: 0 }}
                                    status={
                                        budgetCalculations.isOverBudget
                                            ? "exception"
                                            : "normal"
                                    }
                                    showInfo={true}
                                />
                            </div>
                        </Col>
                        <Col span={4}></Col>
                    </Row>
                    <Divider size="small" className="my-3"/>
                    {currentStep === 2 ? (
                        <CampaignCollectionBreakdown
                            key={clusterId}
                            cluster={cluster}
                            clusterId={clusterId}
                            clientCurrencySymbol={clientCurrencySymbol}
                            handleCampaignUpdate={handleCampaignUpdate}
                            showCombinedView={true}
                            citiesList={citiesList}
                            onAutoSuggestBudget={handleAutoSuggestBudget}
                        />
                    ) : (
                        <>
                            {!cluster?.collections?.length ? (
                                <Space
                                    direction="vertical"
                                    align="center"
                                    style={{
                                        width: "100%",
                                        minHeight: 120,
                                        padding: "12px 24px",
                                        background: "#F5F7FA",
                                        borderRadius: 8,
                                    }}
                                >
                                    <Text className="fw-500" type="secondary">
                                        No campaigns added
                                    </Text>
                                    <Button
                                        type="primary"
                                        onClick={handleAddRow}
                                        icon={<PlusOutlined />}
                                        style={{ width: 180, height: 36 }}
                                        disabled={
                                            !cluster?.totalBudget ||
                                            !cluster?.startDate
                                        }
                                    >
                                        Add Campaigns
                                    </Button>
                                </Space>
                            ) : (
                                <div className="campaign-collections-table-container w-100">
                                    <div
                                        className="d-flex justify-content-between align-items-center w-100"
                                        style={{ marginBottom: 16 }}
                                    >
                                        <Title level={5} style={{ margin: 0 }}>
                                            Campaign Collections
                                        </Title>
                                        <Button
                                            type="primary"
                                            ghost
                                            onClick={handleAddRow}
                                            disabled={disabledAddRow}
                                        >
                                            Add New Row
                                        </Button>
                                    </div>
                                    <RevinitiAntTableWidget
                                        config={getCampaignCreationTableConfig({
                                            handleDuplicate,
                                            handleDelete,
                                            handleFieldChange: (
                                                key: string,
                                                field: string,
                                                value: any,
                                                record?: Record<string, any>
                                            ) =>
                                                handleFieldChange(
                                                    clusterId,
                                                    key,
                                                    field,
                                                    value,
                                                    record
                                                ),
                                            handleAddKeywords: (
                                                record: Record<string, any>
                                            ) => {
                                                // Always pass all keyword types for new cohort selection
                                                // The checkboxes in modal will be all ticked by default
                                                handleAddKeywords(
                                                    clusterId,
                                                    record.id,
                                                    [KeywordTypeValues.BRANDED, KeywordTypeValues.GENERIC, KeywordTypeValues.COMPETITION]
                                                );
                                            },
                                            handleEditCohort: (
                                                record: Record<string, any>,
                                                cohortId: string
                                            ) => {
                                                handleEditCohort(
                                                    clusterId,
                                                    record.id,
                                                    cohortId
                                                );
                                            },
                                            handleAddAssets: (
                                                record: Record<string, any>
                                            ) => {
                                                // console.log("record", record);
                                                handleAddAssets(clusterId, record.id);
                                            },
                                            handleRowExpand,
                                            handleCampaignCollectionBreakdown: (
                                                record: Record<string, any>
                                            ) => {
                                                const rowKey = `${clusterId}-${record.id}`;
                                                setExpandedRowKeys((prev) => {
                                                    if (prev.includes(rowKey)) {
                                                        return prev.filter(
                                                            (key) =>
                                                                key !== rowKey
                                                        );
                                                    } else {
                                                        return [
                                                            ...prev,
                                                            rowKey,
                                                        ];
                                                    }
                                                });
                                                setIsRowExpanded(
                                                    (prev) => !prev
                                                );
                                            },
                                            citiesList,
                                            setSelectedKeywords,
                                            isRowExpanded,
                                            expandedRowKeys,
                                            setExpandedRowKeys,
                                            clientCurrencySymbol,
                                            expandedRowRender: (
                                                record: Record<string, any>
                                            ) => (
                                                <CampaignCollectionBreakdown
                                                    cluster={record.cluster}
                                                    clusterId={record.clusterId}
                                                    clientCurrencySymbol={
                                                        clientCurrencySymbol
                                                    }
                                                    collectionId={record.id}
                                                    handleCampaignUpdate={
                                                        handleCampaignUpdate
                                                    }
                                                    citiesList={citiesList}
                                                    onAutoSuggestBudget={handleAutoSuggestBudget}
                                                />
                                            ),
                                            rowExpandable: (record: Record<string, any>) =>
                                                record.type === "multiple" &&
                                                record.campaigns?.length > 0,
                                        })}
                                        widgetData={{
                                            tableData: (
                                                cluster?.collections || []
                                            ).map((collection) => ({
                                                ...collection,
                                                cluster,
                                                clusterId,
                                            })),
                                        }}
                                        isLoading={false}
                                        handleRowExpand={handleRowExpand}
                                        style={{ height: 450 }}
                                    />
                                </div>
                            )}
                        </>
                    )}
                </Col>
            </Row>
        );
    }
);

export default CreateCollectionsForm;